import { IS_ONPREM, ONPREM_REMOVED_PAGES } from "@shared/config/edition";
/**
 * Sidebar navigation config — what the left rail shows.
 *
 * SEPARATE from the TopNav sections. The sidebar has its own items: Org
 * Dashboard, Asset Discovery (placeholder), Endpoint Inventory,
 * Resource Monitoring, Alerts Panel, and Administration (org_admin only).
 * Agent-scoped pages live in the TopNav, not here — one home per page.
 *
 * Matches the reference mockup's NAV_CONFIG in data/nav.js exactly.
 */

export interface SidebarItem {
  id: string;
  label: string;
  icon: string;
  href: string;
  /** Coming soon — rendered but not clickable. */
  soon?: boolean;
}

export interface SidebarGroup {
  id: string;
  label: string;
  icon: string;
  /** Direct link (no sub-items): the header IS the link. */
  href?: string;
  /** Expandable group: the header toggles this list. */
  items?: SidebarItem[];
  /** Restrict to these roles (org_admin etc). Absent = everyone sees it. */
  roles?: string[];
  /** Restrict to these scope_level values (e.g. ["group"] for group-admin-
   *  only entries). Absent = everyone sees it. Combines ANDed with `roles`
   *  if both are set — the item must satisfy both to appear. */
  scopes?: Array<"platform" | "group" | "org">;
}

const ALL_SIDEBAR_NAV: SidebarGroup[] = [
  // Group-admin only — moved out of the TopNav Browse panel into the sidebar
  // so group admins land on these from the rail (sidebar is their natural
  // home; agent-scoped pages still live in the TopNav). Icons mirror the
  // ones in NAV_ITEMS so the crumb and the sidebar entry match visually.
  { id: "organizations",   label: "Organizations",   icon: "fa-city",
    href: "/organizations",   scopes: ["group"] },
  // { id: "group-dashboard", label: "Group Dashboard", icon: "fa-chart-line",
  //   href: "/group-dashboard", scopes: ["group"] },

  // Org Dashboard — direct link
  
  // Asset Discovery — placeholder group, all "soon"
  // {
  //   id: "assets",
  //   label: "Asset Discovery",
  //   icon: "fa-radar",
  //   items: [
  //     { id: "assets-dash",  label: "Dashboard", icon: "fa-gauge",                 href: "#", soon: true },
  //     { id: "assets-disc",  label: "Discovery", icon: "fa-magnifying-glass-plus", href: "#", soon: true },
  //     { id: "assets-trend", label: "Trends",    icon: "fa-chart-line",            href: "#", soon: true },
  //   ],
  // },

  // { id: "org-dashboard", label: "Organization Dashboard", icon: "fa-gauge-high",
  //   href: "/org-dashboard" },

  // Direct links — a top-level row that navigates
  { id: "registry", label: "Endpoint Inventory", icon: "fa-display",
    href: "/endpoint-inventory" },
  // { id: "resource", label: "Resource Monitoring", icon: "fa-gauge-high",
  //   href: "/resource-monitoring" },
  // { id: "compliance-dash", label: "Compliance Dashboard", icon: "fa-clipboard-check",
  //   href: "/compliance" },

  // Administration — org_admin only
  {
    id: "administration",
    label: "Administration",
    icon: "fa-user-gear",
    roles: ["org_admin", "super_admin"],
    items: [
      // { id: "thresholds",  label: "Alert Thresholds",  icon: "fa-sliders",     href: "/alert-thresholds" },
      { id: "users",       label: "User Management",   icon: "fa-users",       href: "/user-management" },
      { id: "enrollment",  label: "Agent Enrollment",  icon: "fa-download",    href: "/agent-enrollment" },
      { id: "fim-dirs",    label: "FIM Directories",   icon: "fa-folder-tree", href: "/fim-management" },
      { id: "event-volume", label: "Event Volume",     icon: "fa-chart-area",  href: "/event-volume" },
    ],
  },
];

/** Sidebar of this build's edition (on-prem drops the group-scope entries). */
// On-prem only: the Licence page (organisation, limits, dates, "Check now")
// sits in Administration, next to User Management.
const ONPREM_LICENCE_ITEM: SidebarItem = { id: "licence", label: "Licence", icon: "fa-key", href: "/licence" };

export const SIDEBAR_NAV: SidebarGroup[] = IS_ONPREM
  ? ALL_SIDEBAR_NAV.filter((g) => !ONPREM_REMOVED_PAGES.has(g.id)).map((g) =>
      g.id === "administration" ? { ...g, items: [...(g.items ?? []), ONPREM_LICENCE_ITEM] } : g,
    )
  : ALL_SIDEBAR_NAV;
